import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["state", "modal", "portal", "preserveTabOrder", "autoFocusOnShow", "wrapperProps"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useState } from 'react';
import { useSafeLayoutEffect, usePortalRef, useWrapElement } from 'ariakit-utils/hooks';
import { createHook, createComponent, createElement } from 'ariakit-utils/system';
import { useDialog } from '../dialog/dialog.js';
import { P as PopoverContext } from '../__utils-3e6151ed.js';
import { jsx } from 'react/jsx-runtime';
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a popover element.
 * @see https://ariakit.org/components/popover
 * @example
 * ```jsx
 * const state = usePopoverState();
 * const props = usePopover({ state });
 * <Role {...props}>Popover</Role>
 * ```
 */

var usePopover = createHook(function (_ref) {
  var state = _ref.state,
      _ref$modal = _ref.modal,
      modal = _ref$modal === void 0 ? false : _ref$modal,
      _ref$portal = _ref.portal,
      portal = _ref$portal === void 0 ? !!modal : _ref$portal,
      _ref$preserveTabOrder = _ref.preserveTabOrder,
      preserveTabOrder = _ref$preserveTabOrder === void 0 ? true : _ref$preserveTabOrder,
      _ref$autoFocusOnShow = _ref.autoFocusOnShow,
      autoFocusOnShow = _ref$autoFocusOnShow === void 0 ? true : _ref$autoFocusOnShow,
      wrapperProps = _ref.wrapperProps,
      props = _objectWithoutProperties(_ref, _excluded);

  var popoverRef = state.popoverRef; // Makes sure the wrapper element that's passed to popper has the same
  // z-index as the popover element so users only need to set the z-index
  // once.

  useSafeLayoutEffect(function () {
    var wrapper = popoverRef.current;
    var popover = state.contentElement;
    if (!wrapper) return;
    if (!popover) return;
    wrapper.style.zIndex = getComputedStyle(popover).zIndex;
  }, [popoverRef, state.contentElement]); // We have to wait for the popover to be positioned before we can move
  // focus, otherwise there may be scroll jumps. See popover-standalone
  // example test-browser file.

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      canAutoFocusOnShow = _useState2[0],
      setCanAutoFocusOnShow = _useState2[1];

  var _usePortalRef = usePortalRef(portal, props.portalRef),
      portalRef = _usePortalRef.portalRef,
      domReady = _usePortalRef.domReady;

  useSafeLayoutEffect(function () {
    var _state$contentElement;

    if (!domReady) return;
    if (!state.mounted) return;
    if (!((_state$contentElement = state.contentElement) != null && _state$contentElement.isConnected)) return;
    var raf = requestAnimationFrame(function () {
      setCanAutoFocusOnShow(true);
    });
    return function () {
      cancelAnimationFrame(raf);
    };
  }, [domReady, state.mounted, state.contentElement]); // Wrap our element in a div that will be used to position the popover.
  // This way the user doesn't need to override the popper's position to
  // create animations.

  props = useWrapElement(props, function (element) {
    return /*#__PURE__*/jsx("div", _objectSpread(_objectSpread({
      role: "presentation"
    }, wrapperProps), {}, {
      style: _objectSpread({
        position: state.fixed ? "fixed" : "absolute",
        top: 0,
        left: 0
      }, wrapperProps == null ? void 0 : wrapperProps.style),
      ref: popoverRef,
      children: element
    }));
  }, [state.fixed, popoverRef, wrapperProps]);
  props = useWrapElement(props, function (element) {
    return /*#__PURE__*/jsx(PopoverContext.Provider, {
      value: state,
      children: element
    });
  }, [state]);
  props = _objectSpread(_objectSpread({}, props), {}, {
    style: _objectSpread({
      position: "relative"
    }, props.style)
  });
  props = useDialog(_objectSpread(_objectSpread({
    state: state,
    modal: modal,
    preserveTabOrder: preserveTabOrder,
    portal: portal,
    autoFocusOnShow: canAutoFocusOnShow && autoFocusOnShow
  }, props), {}, {
    portalRef: portalRef
  }));
  return props;
});
/**
 * A component that renders a popover element.
 * @see https://ariakit.org/components/popover
 * @example
 * ```jsx
 * const popover = usePopoverState();
 * <PopoverDisclosure state={popover}>Disclosure</PopoverDisclosure>
 * <Popover state={popover}>Popover</Popover>
 * ```
 */

var Popover = createComponent(function (props) {
  var htmlProps = usePopover(props);
  return createElement("div", htmlProps);
});
export { Popover, usePopover };